import React from 'react';

export const DEFAULT_APPLICATION_FIELDS = [
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'years_experience', label: 'Years of sales experience', type: 'number' },
  { key: 'closing_rate', label: 'Your closing rate %', type: 'number' },
  { key: 'linkedin_url', label: 'LinkedIn profile URL', type: 'url' },
  { key: 'resume', label: 'Resume (PDF)', type: 'file' },
  { key: 'solar_experience', label: 'Solar/renewable experience', type: 'bool' },
  { key: 'door_to_door', label: 'Door-to-door experience', type: 'bool' },
  { key: 'has_vehicle', label: 'Reliable vehicle', type: 'bool' },
  { key: 'bilingual', label: 'Bilingual', type: 'bool' },
  { key: 'full_time', label: 'Full-time availability', type: 'bool' },
  { key: 'cover_note', label: 'Why are you a great fit?', type: 'textarea' },
];

export const mergeFields = (saved) => DEFAULT_APPLICATION_FIELDS.map(base => {
  const f = (saved || []).find(x => x.key === base.key) || {};
  return { ...base, label: f.label || base.label, enabled: f.enabled !== false, mandatory: !!f.mandatory };
});

const Toggle = ({ on, onClick, testid, title }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
    className={`relative w-9 h-5 rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}>
    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

export function ApplicationFormEditor({ questions, fields, onQuestions, onFields }) {
  const qs = questions || [];
  const fs = mergeFields(fields);

  const addQ = () => onQuestions([...qs, { question: '', type: 'text', weight: 1, ideal: '', mandatory: false }]);
  const setQ = (i, patch) => onQuestions(qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const rmQ = (i) => onQuestions(qs.filter((_, idx) => idx !== i));
  const moveQ = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= qs.length) return;
    const copy = [...qs];
    [copy[i], copy[to]] = [copy[to], copy[i]];
    onQuestions(copy);
  };
  const setF = (key, patch) => onFields(fs.map(f => (f.key === key ? { ...f, ...patch } : f)));

  return (
    <div className="space-y-4" data-testid="application-form-editor">
      <div className="bg-surface-alt rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase text-text-secondary"><i className="fa-solid fa-clipboard-question mr-1.5 text-sky-600" />Screening Questions</p>
          <button type="button" onClick={addQ} className="btn-secondary text-[11px] py-1" data-testid="add-question-btn"><i className="fa-solid fa-plus mr-1" />Add question</button>
        </div>
        {qs.length === 0 && <p className="text-[11px] text-text-muted opacity-70">No screening questions — applicants apply directly.</p>}
        <div className="space-y-2">
          {qs.map((q, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl p-2.5 space-y-2" data-testid="question-row">
              <div className="flex gap-2 items-center">
                <input placeholder="Question shown on the apply form" value={q.question} onChange={e => setQ(i, { question: e.target.value })} className="input-field text-xs flex-1" data-testid={`question-text-${i}`} />
                <button type="button" onClick={() => moveQ(i, -1)} className="text-text-muted px-1" title="Move up" data-testid={`question-up-${i}`}><i className="fa-solid fa-arrow-up text-[11px]" /></button>
                <button type="button" onClick={() => moveQ(i, 1)} className="text-text-muted px-1" title="Move down" data-testid={`question-down-${i}`}><i className="fa-solid fa-arrow-down text-[11px]" /></button>
                <button type="button" onClick={() => rmQ(i)} className="text-rose-500 px-1" title="Remove question" data-testid={`question-remove-${i}`}><i className="fa-solid fa-xmark" /></button>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <select value={q.type} onChange={e => setQ(i, { type: e.target.value })} className="input-field text-xs w-32" data-testid={`question-type-${i}`}>
                  <option value="text">Short text</option><option value="textarea">Long text</option>
                  <option value="boolean">Yes / No</option><option value="number">Number</option>
                  <option value="select">Multiple choice</option>
                </select>
                {q.type === 'select' && (
                  <input placeholder="Choices, comma separated" value={q.options_raw ?? (q.options || []).join(', ')} onChange={e => setQ(i, { options_raw: e.target.value, options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })} className="input-field text-xs flex-1 min-w-[180px]" data-testid={`question-options-${i}`} />
                )}
                <input placeholder="Weight" type="number" min="1" value={q.weight} onChange={e => setQ(i, { weight: e.target.value })} className="input-field text-xs w-20" title="Scoring weight" />
                <input placeholder="Ideal answer" value={q.ideal || ''} onChange={e => setQ(i, { ideal: e.target.value })} className="input-field text-xs w-28" title="Ideal answer (true / number)" />
                <input placeholder="Helper text (optional)" value={q.help_text || ''} onChange={e => setQ(i, { help_text: e.target.value })} className="input-field text-xs flex-1 min-w-[140px]" />
                <label className="flex items-center gap-2 text-[11px] font-bold uppercase text-text-muted">
                  Required
                  <Toggle on={!!q.mandatory} onClick={() => setQ(i, { mandatory: !q.mandatory })} testid={`question-mandatory-${i}`} title="Make this question mandatory" />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface-alt rounded-xl p-4">
        <p className="text-xs font-bold uppercase text-text-secondary mb-1"><i className="fa-solid fa-sliders mr-1.5 text-violet-600" />Apply-Now Form Fields</p>
        <p className="text-[11px] text-text-muted mb-3">Turn any field off to remove it from the careers application, rename its label, or make it mandatory. Name and email are always required.</p>
        <div className="space-y-1.5">
          {fs.map(f => (
            <div key={f.key} className="flex flex-wrap items-center gap-2 bg-surface border border-border rounded-lg px-2.5 py-2" data-testid={`app-field-${f.key}`}>
              <Toggle on={f.enabled} onClick={() => setF(f.key, { enabled: !f.enabled, mandatory: f.enabled ? false : f.mandatory })} testid={`app-field-enabled-${f.key}`} title="Show on the application" />
              <input value={f.label} onChange={e => setF(f.key, { label: e.target.value })} className="input-field text-xs flex-1 min-w-[160px]" data-testid={`app-field-label-${f.key}`} />
              <span className="text-[10px] uppercase font-bold text-text-muted w-16">{f.type}</span>
              <label className={`flex items-center gap-2 text-[11px] font-bold uppercase ${f.enabled ? 'text-text-muted' : 'text-text-muted opacity-40'}`}>
                Required
                <Toggle on={f.mandatory && f.enabled} onClick={() => f.enabled && setF(f.key, { mandatory: !f.mandatory })} testid={`app-field-mandatory-${f.key}`} title="Make this field mandatory" />
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
